/**
 * Scout v0 — export panel (P19, §1.2 stage 11).
 *
 * The export screen: the operator picks a completed run, toggles the scope
 * (A+B usable default, or include C/D for review), previews the first N rows
 * before downloading, then downloads the CSV. The export history audit trail
 * (who/when/scope/rows/downloads) is shown beneath.
 *
 * One lane, two interfaces (CLI + UI): the export UI wraps the same P03 export
 * lane (assembleExportEntities + renderExport) the CLI uses. Write-back
 * semantics preserved: export does NOT re-write touch_history clocks (the QA
 * lane already did); re-export is idempotent (identical CSV + upserted history
 * row). The download hits the GET /download endpoint directly (text/csv, not
 * JSON), so it is opened via the API URL — the server regenerates the CSV on
 * every download, so it never serves stale grades after QA overrides.
 */

import { useMemo, useState } from 'react';
import {
  useScoutRuns,
  useExportPreview,
  useGenerateExport,
  useExportHistory,
} from '@/lib/scout-queries';
import { scoutExportDownloadEndpoint } from '@/lib/api-contract';
import { API_V2_URL } from '@/lib/api';
import { EmptyState, ErrorBanner, LoadingSpinner } from '@/components/ui';
import { getErrorMessage } from '@/lib/queries';
import { RunStatusBadge } from './RunStatusBadge';
import type { ExportScope, RunSummary } from '@/types/scout';

function formatTimestamp(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A run is exportable when it is completed (has stage artifacts to export). */
function isExportable(run: RunSummary): boolean {
  return run.status === 'completed';
}

function scopeLabel(scope: ExportScope): string {
  return scope === 'cd' ? 'A+B+C+D (review)' : 'A+B (usable)';
}

/** Build the full download URL (the GET /download endpoint returns text/csv). */
function downloadUrl(runId: string, scope: ExportScope): string {
  return `${API_V2_URL}${scoutExportDownloadEndpoint(runId, scope)}`;
}

function PreviewTable({ rows }: { rows: { companyName: string; domain: string | null; jobTitle: string; contactName: string | null; contactEmail: string | null; grade: string; catchallRisk: boolean; routeStatus: string; rowRef: string }[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted italic">No rows in scope - the CSV will contain only the header.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted border-b border-border">
            <th className="py-2 pr-3 font-medium">Company</th>
            <th className="py-2 pr-3 font-medium">Domain</th>
            <th className="py-2 pr-3 font-medium">Job</th>
            <th className="py-2 pr-3 font-medium">Contact</th>
            <th className="py-2 pr-3 font-medium">Grade</th>
            <th className="py-2 pr-3 font-medium">Route</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.rowRef} className="border-b border-border/50">
              <td className="py-2 pr-3 text-text">{row.companyName}</td>
              <td className="py-2 pr-3 text-muted font-mono text-xs">{row.domain ?? '-'}</td>
              <td className="py-2 pr-3 text-text">{row.jobTitle}</td>
              <td className="py-2 pr-3 text-muted text-xs">
                {row.contactName ? `${row.contactName} · ${row.contactEmail ?? '-'}` : '-'}
              </td>
              <td className="py-2 pr-3 text-text font-mono">{row.grade}</td>
              <td className="py-2 pr-3 text-muted text-xs">{row.routeStatus}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTable({ exports }: { exports: { exportId: string; scope: ExportScope; exportedRows: number; totalLeads: number; downloadCount: number; exportedBy: string; exportedAt: string }[] }) {
  if (exports.length === 0) {
    return <p className="text-sm text-muted italic">No exports recorded for this run yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted border-b border-border">
            <th className="py-2 pr-3 font-medium">Scope</th>
            <th className="py-2 pr-3 font-medium">Rows</th>
            <th className="py-2 pr-3 font-medium">Total leads</th>
            <th className="py-2 pr-3 font-medium">Downloads</th>
            <th className="py-2 pr-3 font-medium">By</th>
            <th className="py-2 pr-3 font-medium">Exported at</th>
          </tr>
        </thead>
        <tbody>
          {exports.map((entry) => (
            <tr key={entry.exportId} className="border-b border-border/50">
              <td className="py-2 pr-3 text-text">{scopeLabel(entry.scope)}</td>
              <td className="py-2 pr-3 text-muted">{entry.exportedRows}</td>
              <td className="py-2 pr-3 text-muted">{entry.totalLeads}</td>
              <td className="py-2 pr-3 text-muted">{entry.downloadCount}</td>
              <td className="py-2 pr-3 text-muted font-mono text-xs">{entry.exportedBy}</td>
              <td className="py-2 pr-3 text-muted">{formatTimestamp(entry.exportedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RunExportCard({
  run,
  isSelected,
  onSelect,
}: {
  run: RunSummary;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <article
      className={`card cursor-pointer transition-colors duration-fast ${isSelected ? 'ring-1 ring-secondary' : 'hover:bg-surface'}`}
      aria-current={isSelected || undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      tabIndex={0}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <p className="text-xs text-muted font-mono">{run.runId}</p>
          <p className="text-sm text-muted">{formatTimestamp(run.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RunStatusBadge status={run.status} />
          {isExportable(run) && <span className="badge badge-primary">exportable</span>}
        </div>
      </div>
      <p className="text-xs text-muted font-mono">{run.criteriaVersion || '-'}</p>
    </article>
  );
}

export function ExportPanel() {
  const { data: runs, isLoading: runsLoading, error: runsError, refetch: refetchRuns } = useScoutRuns();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [scope, setScope] = useState<ExportScope>('ab');

  // APP-W01d: run LIST changes prevent stale selection — when the selected
  // run disappears (new generation, list refresh) the selection is DERIVED
  // to null so no stale preview/generate/histories stay wired to a dead row.
  // (Derived at render — no state-write effect, no cascading render.)
  //
  // W01d-D6 correction: `effectiveRunId` is the ONE action-safe run-selection
  // authority. Every surface that REQUIRES a currently valid selection — the
  // scope/action card, its Generate action, the download href, the preview and
  // the history — reads THIS value, never the raw `selectedRunId`. Gating only
  // the data hooks on derivation while the action surfaces still read raw state
  // left a departed run's download link and Generate action live (a stale
  // deep-link and a malformed empty-run URL). Raw state remains solely the
  // operator's selection INTENT; validity is always derived from data.
  const runIdSet = useMemo(
    () => new Set((runs ?? []).map((r) => r.runId)),
    [runs],
  );
  const effectiveRunId = selectedRunId !== null && runIdSet.has(selectedRunId)
    ? selectedRunId
    : null;
  const runId = effectiveRunId ?? '';
  const { data: preview, isLoading: previewLoading, error: previewError, refetch: refetchPreview } = useExportPreview(
    runId,
    scope,
  );
  const generate = useGenerateExport(runId, scope);
  const { data: history } = useExportHistory(runId);

  const exportableRuns = (runs ?? []).filter(isExportable);

  if (runsError) {
    return (
      <section className="card">
        <ErrorBanner message={getErrorMessage(runsError)} retryable={runsError.retryable} onRetry={() => refetchRuns()} />
      </section>
    );
  }

  if (runsLoading) {
    return (
      <section className="card">
        <LoadingSpinner className="py-6" />
      </section>
    );
  }

  return (
    <div className="space-y-6">
      {/* Completed runs list */}
      <section className="card" aria-labelledby="export-runs-heading">
        <h2 id="export-runs-heading" className="text-lg font-semibold text-text mb-3">
          Completed runs
        </h2>
        {exportableRuns.length === 0 ? (
          <EmptyState
            title="No completed runs"
            description="Launch and complete a run, then review it at the QA gate before exporting."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {exportableRuns.map((run) => (
              <RunExportCard
                key={run.runId}
                run={run}
                isSelected={run.runId === selectedRunId}
                onSelect={() => setSelectedRunId(run.runId)}
              />
            ))}
          </div>
        )}
      </section>

      {effectiveRunId && (
        <>
          {/* Scope toggle + actions */}
          <section className="card" aria-labelledby="export-scope-heading">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 id="export-scope-heading" className="text-lg font-semibold text-text">
                  Export
                </h2>
                <p className="text-xs text-muted mt-0.5 font-mono">{effectiveRunId}</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted" htmlFor="export-scope">
                  Scope
                </label>
                <select
                  id="export-scope"
                  className="input text-sm"
                  value={scope}
                  onChange={(e) => setScope(e.target.value as ExportScope)}
                >
                  <option value="ab">A+B (usable)</option>
                  <option value="cd">A+B+C+D (review)</option>
                </select>
              </div>
            </div>

            {/* Scope note — the locked write-back semantics. */}
            <p className="text-xs text-muted mb-4">
              {scope === 'ab'
                ? 'Human-approved A+B leads only. Unreviewed rows are excluded, including during partial QA.'
                : 'Review scope (A+B+C+D), including unreviewed rows. This is an evidence file, not the usable-lead list.'}{' '}
              Re-export is idempotent - it produces the same CSV and does not re-write touch-history clocks.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
              >
                {generate.isPending ? 'Generating…' : 'Generate CSV'}
              </button>
              <a
                type="button"
                className="btn btn-secondary"
                href={downloadUrl(runId, scope)}
                // The download endpoint returns text/csv with Content-Disposition
                // attachment — the browser downloads it. The server regenerates the
                // CSV on every download, so the link always serves fresh grades.
                // `runId` is the DERIVED effective selection: this anchor is only
                // reachable when a currently-valid run is selected, so it can never
                // emit a stale download URL for a departed run.
              >
                Download CSV
              </a>
              {generate.isSuccess && (
                <span className="text-xs text-primary self-center">
                  ✓ {generate.data.exportedRows} rows generated
                </span>
              )}
              {generate.isError && (
                <span className="text-xs text-warning self-center">
                  {getErrorMessage(generate.error)}
                </span>
              )}
            </div>
          </section>

          {/* Pre-download column preview */}
          <section className="card" aria-labelledby="export-preview-heading">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 id="export-preview-heading" className="text-base font-semibold text-text">
                  Preview
                </h3>
                <p className="text-xs text-muted mt-0.5">
                  First {preview?.previewRows.length ?? 0} rows of {preview?.totalRows ?? 0} in scope
                </p>
              </div>
              {preview && (
                <div className="flex items-center gap-2 text-xs text-muted">
                  <span>A: {preview.gradeCounts.A ?? 0}</span>
                  <span>B: {preview.gradeCounts.B ?? 0}</span>
                  <span>C: {preview.gradeCounts.C ?? 0}</span>
                  <span>D: {preview.gradeCounts.D ?? 0}</span>
                  {preview.catchallFlagged > 0 && (
                    <span className="badge badge-warning" title="catchall risk flag, not a kill">
                      {preview.catchallFlagged} catchall
                    </span>
                  )}
                </div>
              )}
            </div>

            {previewError ? (
              <ErrorBanner message={getErrorMessage(previewError)} retryable={previewError.retryable} onRetry={() => refetchPreview()} />
            ) : previewLoading ? (
              <LoadingSpinner className="py-6" />
            ) : preview ? (
              <PreviewTable rows={preview.previewRows} />
            ) : null}
          </section>

          {/* Export history audit trail */}
          {history && history.exports.length > 0 && (
            <section className="card" aria-labelledby="export-history-heading">
              <h3 id="export-history-heading" className="text-base font-semibold text-text mb-3">
                Export history
              </h3>
              <HistoryTable exports={history.exports} />
            </section>
          )}
        </>
      )}
    </div>
  );
}